import BackendService from 'BackendService';
import { createContext, useContext } from 'react';
import Contact from './Contact';
import TokenData from './TokenData';

export interface CommonLoggedInData {
  email: string;
  contact: Contact | null;
  onAssetTokenUpdate(listener: (token: TokenData) => void): void;
  get sidebarExpanded(): boolean;
  set sidebarExpanded(expanded: boolean);
}

export class CommonLoggedInDataImpl implements CommonLoggedInData {
  private assetTokenListeners: ((token: TokenData) => void)[];
  private _sidebarExpanded: boolean;

  constructor(
    public email: string,
    public contact: Contact | null,
  ) {
    this.contact = contact;
    this.email = email;
    this.assetTokenListeners = [];
    this._sidebarExpanded = false;
  }

  set assetToken(token: TokenData) {
    this.assetTokenListeners.forEach((listener) => listener(token));
  }

  onAssetTokenUpdate(listener: (token: TokenData) => void): void {
    this.assetTokenListeners.push(listener);
  }

  set sidebarExpanded(expanded: boolean) {
    this._sidebarExpanded = expanded;
  }

  get sidebarExpanded(): boolean {
    return this._sidebarExpanded;
  }
}

export class CommonLoggedInDataWithToken {
  private tokenData: TokenData | null;
  private tokenRequestInProgress: boolean;
  private tokenRequestPromise: Promise<string> | null;
  public setSidebarExpandedCallback?: (value: boolean) => void;

  constructor(private data: CommonLoggedInData | null) {
    this.tokenData = null;
    this.tokenRequestInProgress = false;
    this.tokenRequestPromise = null;
  }

  get loggedInData(): CommonLoggedInData | null {
    return this.data;
  }

  get isLoggedIn(): boolean {
    return !!this.data;
  }

  getAssetToken(): Promise<string> {
    if (this.tokenRequestInProgress) {
      return this.tokenRequestPromise as Promise<string>;
    }

    this.tokenRequestPromise = new Promise((resolve, reject) => {
      if (!this.data) {
        reject(new Error('Not logged in'));
      }

      const now = new Date();
      const expireTime = new Date(this.tokenData?.token?.res?.data?.expireTime || 0);

      if (!this.tokenData || now.getTime() > expireTime.getTime() - 10000) {
        this.tokenRequestInProgress = true;

        new BackendService()
          .setupAssetStorageWithRetry(true)
          .then((newTokenData: TokenData) => {
            this.tokenData = newTokenData;
            this.tokenRequestInProgress = false;
            resolve(newTokenData.token.token);
          })
          .catch((error) => {
            this.tokenRequestInProgress = false;
            reject(error);
          });
      } else {
        resolve(this.tokenData.token.token);
      }
    });

    return this.tokenRequestPromise;
  }

  get sidebarExpanded(): boolean {
    return this.data?.sidebarExpanded ?? false;
  }

  set sidebarExpanded(expanded: boolean) {
    if (!this.data) {
      return;
    }

    this.data!.sidebarExpanded = expanded;
    this.setSidebarExpandedCallback?.(expanded);
  }
}

export const CommonLoggedInDataContext = createContext<CommonLoggedInDataWithToken | null>(null);

export function useCommonLoggedInData(): CommonLoggedInDataWithToken | null {
  return useContext(CommonLoggedInDataContext);
}
